import { useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { Rock, RockCategory, RockFilters as RockFiltersType, SharedProps, User, PageProps } from '../../../types'
import Layout from '../../../components/Layout'
import RockTable from '../../../components/rocks/RockTable'
import RockFilters from '../../../components/rocks/RockFilters'
import RockCheckInModal from '../../../components/rocks/RockCheckInModal'
import RockPlanner from '../../../components/rocks/RockPlanner'
import CategoryManager from '../../../components/rocks/CategoryManager'

interface Props extends PageProps {
  team: { id: number; name: string; description: string | null }
  active_tab: string
  rocks: Rock[]
  categories: RockCategory[]
  team_members: User[]
  filters: RockFiltersType
  current_quarter: string
  can_create: boolean
  can_manage_categories: boolean
}

export default function RocksIndex({
  team, active_tab, rocks, categories, team_members, filters, current_quarter, can_create, can_manage_categories, flash
}: Props) {
  const { current_user } = usePage<{ props: SharedProps }>().props as unknown as SharedProps
  // Task #614: "+ New Rock" opens the F.A.S.T. Rock Planner in create mode.
  // Once its first autosave creates the rock, `createdRockId` lets the same
  // open panel bind to the real record (milestones, actions, check-ins).
  const [creatingRock, setCreatingRock] = useState(false)
  const [createdRockId, setCreatedRockId] = useState<number | null>(null)
  const [checkInRock, setCheckInRock] = useState<Rock | null>(null)
  const [detailRock, setDetailRock] = useState<Rock | null>(null)
  const [showCategories, setShowCategories] = useState(false)

  const activeTab = filters.tab || 'active'
  const isBacklog = activeTab === 'backlog'

  const handleTabChange = (tab: string) => {
    router.get(`/teams/${team.id}/rocks`, { tab, group_by: filters.group_by }, { preserveState: true })
  }

  const detailRockData = detailRock ? rocks.find(r => r.id === detailRock.id) || detailRock : null

  return (
    <Layout team={{ id: team.id, name: team.name, member_count: team_members.length }} activeTab={active_tab}>
      <Head title={`Rocks - ${team.name} - Pinnacle`} />

      <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-text-primary font-heading">F.A.S.T. Rocks</h1>
            <p className="text-base text-text-secondary">{current_quarter}</p>
          </div>

          <div className="flex items-center gap-2">
            {can_manage_categories && (
              <button
                onClick={() => setShowCategories(true)}
                className="px-4 py-2 border border-stone/30 rounded-lg text-base text-text-secondary hover:bg-[#F8F9FA] font-heading"
              >
                Edit Categories
              </button>
            )}
            {can_create && (
              <button
                onClick={() => { setCreatedRockId(null); setCreatingRock(true) }}
                className="px-5 py-2.5 bg-pinnacle-red text-white rounded-lg text-base font-semibold hover:bg-pinnacle-red/90 font-heading"
              >
                + New Rock
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-1 mb-4">
          <button
            onClick={() => handleTabChange('active')}
            className={`px-4 py-2 rounded-lg text-base font-medium transition-colors ${
              activeTab === 'active'
                ? 'bg-charcoal text-white'
                : 'text-text-secondary hover:bg-[#351E1D]/5'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => handleTabChange('backlog')}
            className={`px-4 py-2 rounded-lg text-base font-medium transition-colors ${
              activeTab === 'backlog'
                ? 'bg-charcoal text-white'
                : 'text-text-secondary hover:bg-[#351E1D]/5'
            }`}
          >
            Backlog
          </button>
        </div>

        <div className="mb-4">
          <RockFilters teamId={team.id} filters={filters} />
        </div>

        <RockTable
          rocks={rocks}
          teamId={team.id}
          groupBy={filters.group_by || 'none'}
          isBacklog={isBacklog}
          teamMembers={team_members}
          categories={categories}
          onViewDetail={setDetailRock}
          canReorder={can_create && !isBacklog}
          showCheckInNote
        />

      {creatingRock && (
        <RockPlanner
          key="rock-planner-create"
          rock={createdRockId ? rocks.find(r => r.id === createdRockId) || null : null}
          teamId={team.id}
          teamMembers={team_members}
          categories={categories}
          currentUserId={current_user?.id}
          onCreated={setCreatedRockId}
          onClose={() => { setCreatingRock(false); setCreatedRockId(null) }}
          onCheckIn={(r) => { setCreatingRock(false); setCreatedRockId(null); setCheckInRock(r) }}
        />
      )}

      {checkInRock && (
        <RockCheckInModal
          rock={checkInRock}
          teamId={team.id}
          onClose={() => setCheckInRock(null)}
        />
      )}

      {detailRockData && (
        <RockPlanner
          rock={detailRockData}
          teamId={team.id}
          teamMembers={team_members}
          categories={categories}
          onClose={() => setDetailRock(null)}
          onCheckIn={(r) => { setDetailRock(null); setCheckInRock(r) }}
        />
      )}

      {showCategories && (
        <CategoryManager
          teamId={team.id}
          categories={categories}
          onClose={() => setShowCategories(false)}
        />
      )}
    </Layout>
  )
}
